import {
  TableRow,
  TableCell,
  Typography,
  ICONS_ENUM,
  Button,
  Icon,
  Tooltip
} from '@sanlam-glacier/app-common/components';
import styles from './section-14-table.module.scss';
import clsx from 'clsx';
import { Section14UrgencyTab } from 'modules/my-practice/types/section-14/section-14.enums';
import { Link } from 'react-router-dom';
import { InformationIndicator } from '../../atoms/information-indicator/InformationIndicator';
import { StatusPill } from '../../atoms/status-pill/StatusPill';
import { useState } from 'react';
import { Section14ClientRenewalModel } from 'bff';

type ReportTableProps = {
  row: Section14ClientRenewalModel;
  activeTab: Section14UrgencyTab;
  isExpanded: boolean;
  toggleExpand: (clientId: number) => void;
};

export const Section14TableRow = ({
  row,
  activeTab,
  isExpanded,
  toggleExpand
}: ReportTableProps) => {
  const testComponentName = 'Section14TableRow';

  const [displayTooltip, setDisplayTooltip] = useState(false);
  const totalValue = row.investments.reduce((sum, inv) => sum + inv.investmentValue, 0);

  const earliestDate = row.investments.reduce((earliest, inv) =>
    new Date(inv.feeRenewalDate) < new Date(earliest.feeRenewalDate) ? inv : earliest
  );

  const mostLateInvestment = row.investments.reduce((prev, current) => {
    if (prev?.daysUntilDue === undefined) return current;
    if (current.daysUntilDue === undefined) return prev;

    return current.daysUntilDue < prev.daysUntilDue ? current : prev;
  }, row.investments[0]);

  const handleDownload = () => {
    // TODO: Implement download logic
  };

  const handleHoverOverTableRow = () => {
    setDisplayTooltip(true);
  };

  return (
    <>
      <TableRow
        onMouseOver={handleHoverOverTableRow}
        onFocus={handleHoverOverTableRow}
        onClick={() => row.investments.length > 1 && toggleExpand(row.clientId)}
        className={clsx(
          styles.clientRow,
          row.investments.length > 1 && isExpanded && (mostLateInvestment?.daysUntilDue ?? 0) < 0
            ? styles.multipleInvestmentsLate
            : row.investments.length > 1 &&
                isExpanded &&
                (mostLateInvestment?.daysUntilDue ?? 0) >= 0
              ? styles.multipleInvestmentsUpcoming
              : undefined,
          isExpanded && styles.expandedLastChildBorder
        )}
        style={{ cursor: row.investments.length > 1 ? 'pointer' : 'default' }}
      >
        <TableCell
          data-testid={`${testComponentName}ClientName`}
          className={clsx(styles.tableCells)}
        >
          <div className={styles.clientNameCell}>
            <Link to={`/clients/${row.clientId}`}>
              <Typography
                color={Typography.COLOR.PRIMARY}
                variant={Typography.VARIANT.B4}
                weight={Typography.WEIGHT.MEDIUM}
              >
                {row.clientName}
              </Typography>
            </Link>
            <InformationIndicator
              activeTab={activeTab}
              lateRenewalsInfo={row.numLateRenewals}
              upcomingRenewalsInfo={row.numUpcomingRenewals}
            />
          </div>
        </TableCell>
        <TableCell
          data-testid={`${testComponentName}multipleInvestmentsIndicator`}
          className={clsx(styles.tableCells)}
        >
          {row.investments.length > 1 ? (
            <div className={styles.investmentDetailsToggle}>
              <Typography variant={Typography.VARIANT.B4} weight={Typography.WEIGHT.MEDIUM}>
                Multiple investments
              </Typography>
              <Icon
                icon={
                  isExpanded ? ICONS_ENUM.NAVIGATION_EXPAND_LESS : ICONS_ENUM.NAVIGATION_EXPAND_MORE
                }
                className={styles.expandIcon}
              />
            </div>
          ) : (
            <>
              <Typography
                data-testid={`${testComponentName}InvestmentNumber`}
                align={Typography.ALIGN.LEFT}
                variant={Typography.VARIANT.B4}
              >
                {row.investments[0]?.investmentNumber}
              </Typography>

              {displayTooltip ? (
                <Tooltip
                  position={Tooltip.POSITION.BOTTOM_START}
                  content={row.investments[0]?.investmentName ?? ''}
                >
                  <span>
                    <Typography
                      data-testid={`${testComponentName}InvestmentDetails`}
                      variant={Typography.VARIANT.B4}
                      color={Typography.COLOR.SECONDARY}
                      className={styles.investmentName}
                    >
                      {row.investments[0]?.investmentName}
                    </Typography>
                  </span>
                </Tooltip>
              ) : (
                <Typography
                  data-testid={`${testComponentName}InvestmentDetails`}
                  variant={Typography.VARIANT.B4}
                  color={Typography.COLOR.SECONDARY}
                  className={styles.investmentName}
                  onMouseLeave={() => setDisplayTooltip(true)}
                >
                  {row.investments[0]?.investmentName}
                </Typography>
              )}
            </>
          )}
        </TableCell>
        <TableCell
          data-testid={`${testComponentName}InvestmentValue`}
          className={clsx(styles.tableCells)}
        >
          <Typography
            align={Typography.ALIGN.RIGHT}
            variant={Typography.VARIANT.B4}
            weight={
              row.investments.length > 1 ? Typography.WEIGHT.MEDIUM : Typography.WEIGHT.NORMAL
            }
          >
            {(row.investments.length > 1 ? totalValue : (row.investments[0]?.investmentValue ?? 0))
              .toLocaleString('en-ZA', {
                style: 'currency',
                currency: 'ZAR',
                minimumFractionDigits: 2
              })
              .replace(/,/g, ' ')}
          </Typography>
        </TableCell>
        <TableCell className={clsx(styles.tableCells)}>
          <Typography
            color={Typography.COLOR.PRIMARY}
            variant={Typography.VARIANT.B4}
            align={Typography.ALIGN.RIGHT}
          >
            {row.investments.length === 1 ? `${row.investments[0]?.intermediaryFee ?? 0}%` : ''}
          </Typography>
        </TableCell>
        <TableCell className={clsx(styles.tableCells)}>
          <Typography align={Typography.ALIGN.RIGHT} variant={Typography.VARIANT.B4}>
            {row.investments.length > 1
              ? earliestDate.feeRenewalDate.toLocaleDateString('en-ZA')
              : row.investments[0]?.feeRenewalDate.toLocaleDateString('en-ZA')}
          </Typography>
        </TableCell>
        <TableCell className={clsx(styles.tableCells)}>
          <Typography align={Typography.ALIGN.LEFT} variant={Typography.VARIANT.B4}>
            {row.investments.length > 1 ? (
              <StatusPill
                daysUntilDue={mostLateInvestment?.daysUntilDue ?? 0}
                status={mostLateInvestment?.renewalStatus ?? '—'}
              />
            ) : (
              <StatusPill
                daysUntilDue={row.investments[0]?.daysUntilDue ?? 0}
                status={row.investments[0]?.renewalStatus ?? '—'}
              />
            )}
          </Typography>
        </TableCell>
        <TableCell className={styles.tableCells}>
          <div className={styles.iconWrapper}>
            <Button
              startIcon={<Icon icon={ICONS_ENUM.ACTION_VISIBILITY} />}
              variant={Button.VARIANT.TERTIARY}
              size={Button.SIZE.SMALL}
              disabled
            />
            <Button
              onClick={handleDownload}
              startIcon={<Icon icon={ICONS_ENUM.FILE_DOWNLOAD} />}
              variant={Button.VARIANT.TERTIARY}
              size={Button.SIZE.SMALL}
              disabled
            />
          </div>
        </TableCell>
      </TableRow>
      {isExpanded &&
        row.investments.map((investment, index) => (
          <TableRow
            key={investment.investmentNumber}
            className={clsx(
              styles.childRow,
              index === row.investments.length - 1 && styles.lastChildRow
            )}
          >
            <TableCell />
            <TableCell className={clsx(styles.tableCells)}>
              <Typography variant={Typography.VARIANT.B4} weight={Typography.WEIGHT.MEDIUM}>
                {investment.investmentNumber}
              </Typography>
              {displayTooltip ? (
                <Tooltip
                  position={Tooltip.POSITION.BOTTOM_START}
                  content={row.investments[0]?.investmentName ?? ''}
                >
                  <span>
                    <Typography
                      variant={Typography.VARIANT.B4}
                      color={Typography.COLOR.SECONDARY}
                      className={styles.investmentName}
                    >
                      {row.investments[0]?.investmentName}
                    </Typography>
                  </span>
                </Tooltip>
              ) : (
                <Typography
                  variant={Typography.VARIANT.B4}
                  color={Typography.COLOR.SECONDARY}
                  className={styles.investmentName}
                  onMouseLeave={() => setDisplayTooltip(true)}
                >
                  {row.investments[0]?.investmentName}
                </Typography>
              )}
            </TableCell>
            <TableCell className={clsx(styles.tableCells)}>
              <Typography
                align={Typography.ALIGN.RIGHT}
                variant={Typography.VARIANT.B4}
                weight={Typography.WEIGHT.NORMAL}
              >
                {investment.investmentValue
                  .toLocaleString('en-ZA', {
                    style: 'currency',
                    currency: 'ZAR',
                    minimumFractionDigits: 2
                  })
                  .replace(/,/g, ' ')}
              </Typography>
            </TableCell>
            <TableCell className={clsx(styles.tableCells)}>
              <Typography
                color={Typography.COLOR.PRIMARY}
                align={Typography.ALIGN.RIGHT}
                variant={Typography.VARIANT.B4}
              >
                {`${investment.intermediaryFee}%`}
              </Typography>
            </TableCell>
            <TableCell className={clsx(styles.tableCells)}>
              <Typography align={Typography.ALIGN.RIGHT} variant={Typography.VARIANT.B4}>
                {investment.feeRenewalDate.toLocaleDateString('en-ZA')}
              </Typography>
            </TableCell>
            <TableCell className={clsx(styles.tableCells)}>
              <StatusPill
                daysUntilDue={investment.daysUntilDue}
                status={investment.renewalStatus}
              />
            </TableCell>
            <TableCell className={styles.tableCells}>
              <div className={styles.iconWrapper}>
                <Button
                  startIcon={<Icon icon={ICONS_ENUM.ACTION_VISIBILITY} />}
                  variant={Button.VARIANT.TERTIARY}
                  size={Button.SIZE.SMALL}
                  disabled
                />
                <Button
                  onClick={handleDownload}
                  startIcon={<Icon icon={ICONS_ENUM.FILE_DOWNLOAD} />}
                  variant={Button.VARIANT.TERTIARY}
                  size={Button.SIZE.SMALL}
                  disabled
                />
              </div>
            </TableCell>
          </TableRow>
        ))}
    </>
  );
};
